import { useCallback } from "react";
import {
  blurEditor,
  focusEditor,
  usePlateSelectors,
} from "@udecode/plate-core";

import { useLabels } from "~/hooks/useLabels";
import { useRect } from "~/hooks/useRect";
import { isMobileNative } from "~/utils/helpers";

import { Divider } from "../divider/Divider";
import { Button } from "../button/Button";
import { TextEditorControl } from "./TextEditorControl";
import {
  INTENT_BULLETED_LIST,
  INTENT_NUMBERED_LIST,
  INTENT_QUOTE,
  INTENT_REDO,
  INTENT_UNDO,
  INTENT_BOLD,
  INTENT_ITALIC,
  INTENT_UNDERLINE,
  INTENT_INDENT,
  INTENT_OUTDENT,
  DEFAULT_LABELS,
} from "./constants";
import { ScrollContainer } from "../scrollContainer/ScrollContainer";

import type { TextEditorControlIntent } from "./TextEditorControl";

export interface TextEditorToolbarProps {
  useSimpleEditor?: boolean;
  id?: string;
  canSubmit?: boolean;
  isSaving?: boolean;
  keyboardIntent?: TextEditorControlIntent;
  onSave?: () => Promise<void>;
}

export function TextEditorToolbar({
  useSimpleEditor = false,
  id,
  canSubmit,
  isSaving,
  keyboardIntent,
  onSave,
}: TextEditorToolbarProps) {
  const { t } = useLabels({ fallback: DEFAULT_LABELS });

  const [actionsRef, { width: actionsWidth }] = useRect<HTMLDivElement>();

  const selectors = usePlateSelectors(id);
  const editor = selectors.editor();
  // const isRendered = selectors.isRendered();

  const handleHideKeyboard = useCallback(() => blurEditor(editor), [editor]);

  const handleShowKeyboard = useCallback(() => focusEditor(editor), [editor]);

  const toolbarControls = (
    <ScrollContainer width={`calc(100% - ${actionsWidth}px - var(--size-1))`}>
      <div className="TextEditorToolbar__controls">
        <TextEditorControl
          label={t("textEditor.bold")}
          intent={INTENT_BOLD}
          isActive={false}
        />
        <TextEditorControl
          label={t("textEditor.italic")}
          intent={INTENT_ITALIC}
          isActive={false}
        />
        <TextEditorControl
          label={t("textEditor.underline")}
          intent={INTENT_UNDERLINE}
          isActive={false}
        />

        <Divider size="sm" orientation="vertical" />

        <TextEditorControl
          label={t("textEditor.bulletedList")}
          intent={INTENT_BULLETED_LIST}
          isActive={false}
        />
        <TextEditorControl
          label={t("textEditor.numberedList")}
          intent={INTENT_NUMBERED_LIST}
          isActive={false}
        />

        <Divider size="sm" orientation="vertical" />

        <TextEditorControl
          label={t("textEditor.indent")}
          intent={INTENT_INDENT}
          isActive={false}
        />
        <TextEditorControl
          label={t("textEditor.outdent")}
          intent={INTENT_OUTDENT}
          isActive={false}
        />

        <Divider size="sm" orientation="vertical" />

        <TextEditorControl
          label={t("textEditor.quote")}
          intent={INTENT_QUOTE}
          isActive={false}
        />

        <Divider size="sm" orientation="vertical" />

        <TextEditorControl
          label={t("textEditor.undo")}
          intent={INTENT_UNDO}
          isActive={false}
        />
        <TextEditorControl
          label={t("textEditor.redo")}
          intent={INTENT_REDO}
          isActive={false}
        />

        {isMobileNative() && (
          <>
            <Divider size="sm" orientation="vertical" />

            {keyboardIntent === "hideKeyboard" && (
              <TextEditorControl
                label={t("textEditor.hideKeyboard")}
                intent={keyboardIntent}
                isActive={false}
                intentOverride={handleHideKeyboard}
              />
            )}
            {keyboardIntent === "showKeyboard" && (
              <TextEditorControl
                label={t("textEditor.showKeyboard")}
                intent={keyboardIntent}
                isActive={false}
                intentOverride={handleShowKeyboard}
              />
            )}
          </>
        )}
      </div>
    </ScrollContainer>
  );

  return (
    <div className="TextEditorToolbar">
      {useSimpleEditor ? <div></div> : toolbarControls}

      <div ref={actionsRef} className="TextEditorToolbar__actions">
        <Button
          size="xs"
          disabled={!canSubmit}
          busy={isSaving}
          onClick={onSave}
        >
          {t("textEditor.save")}
        </Button>
      </div>
    </div>
  );
}
